import { useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useChatRooms } from './useChatRooms';
import { subscribeToViewedChanges } from '../../util/messageViewedTracker';

/**
 * Hook to get the total unread message count for the current user
 * Only counts conversations with unviewed messages (not just unread, but unviewed)
 * Automatically updates when conversations are marked as viewed
 * @returns {number} Total unviewed message count across all chat rooms
 */
export const useUnreadMessagesCount = () => {
  const { user } = useSelector((state) => state?.auth);
  const userId = user?.id;
  const location = useLocation();
  
  const { totalUnreadCount, loading, refetch } = useChatRooms(userId);
  
  // Subscribe to viewed conversation changes
  useEffect(() => {
    if (!userId) return;
    
    const unsubscribe = subscribeToViewedChanges(() => {
      // When a conversation is marked as viewed, refetch to get latest unread counts
      if (refetch) {
        refetch();
      }
    });
    
    return unsubscribe;
  }, [userId, refetch]);
  
  // Refetch when navigating to messages page
  useEffect(() => {
    if (location.pathname.includes('/messages')) {
      // Small delay to ensure message was marked as read first
      const timer = setTimeout(() => {
        if (refetch) {
          refetch();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, refetch]);

  // As a safety net, periodically refetch to keep counts fresh
  useEffect(() => {
    if (!userId || !refetch) return;
    const intervalId = setInterval(() => {
      refetch();
    }, 5000); // 5s polling for near‑real‑time updates
    return () => clearInterval(intervalId);
  }, [userId, refetch]);
  
  // Count only conversations with unviewed messages
  const unviewedCount = useMemo(() => {
    if (loading || !userId) return 0;
    return totalUnreadCount || 0;
  }, [loading, userId, totalUnreadCount]);
  
  return unviewedCount;
};

export default useUnreadMessagesCount;

